/**
 * Shared helpers for the review components.
 *
 * The reviews API returns `time` either as an ISO date string ("2026-08-05")
 * or as a unix timestamp in seconds (the local mock endpoint), so both are
 * handled here. Review text may contain literal <br> tags.
 */

const MONTH_DAY_YEAR = { day: 'numeric', month: 'long', year: 'numeric' };

export const getDocumentLocale = () =>
  (typeof document !== 'undefined' && document.documentElement.lang) || 'nl';

/** YYYY-MM-DD in local time — toISOString() would shift the day in CEST. */
const toIsoDate = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

export const parseReviewDate = (time) => {
  if (time === null || time === undefined || time === '') {
    return null;
  }

  // Unix timestamp in seconds (number or numeric string)
  if (typeof time === 'number' || /^\d+$/.test(String(time).trim())) {
    const date = new Date(Number(time) * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // ISO date ("2026-08-05" or "2026-08-05 14:30:00"). Build the date from its
  // parts so it is not shifted by the visitor's timezone.
  const match = String(time).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(time);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatReviewDate = (time, locale) => {
  const date = parseReviewDate(time);

  if (!date) {
    return null;
  }

  return {
    dateTime: toIsoDate(date),
    label: date.toLocaleDateString(locale || getDocumentLocale(), MONTH_DAY_YEAR),
  };
};

/** Turn the <br> tags the API sends into real line breaks. */
export const normaliseReviewText = (text) =>
  String(text ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const toRating = (rating) => {
  const parsed = Math.round(Number(rating));
  return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 5) : 0;
};
